import { useEffect, useRef } from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import { useRouter } from '@zapier/toolbox-redux-framework';

import * as utils from './utils';
import {
  GetTasksHeldBanner,
  GetTasksHeldBanner_account_heldTasksByReason as Aggregation,
} from './types/GetTasksHeldBanner';

type Input = {
  accountId: number;
};

const tasksHeldBannerQuery = gql`
  query GetTasksHeldBanner($accountId: Int!) {
    account(id: $accountId) {
      id
      heldTasksByReason {
        reasons {
          auths {
            id
            title
          }
          id
          reason
          total
        }
        startsExpiringOn
        totalHeldTaskCount
      }
      lastChargeFail
      plan {
        hasPremiumApps
        id
        tasks
      }
    }
  }
`;

function useHeldTasks({ accountId }: Input) {
  const previous = useRef<Aggregation | undefined>();
  const { pathname } = useRouter();

  const { data, loading, refetch } = useQuery<GetTasksHeldBanner>(
    tasksHeldBannerQuery,
    {
      variables: {
        accountId,
      },
    }
  );

  // Reload held task data when the route changes
  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const account = data?.account;
  const aggregation = account?.heldTasksByReason;
  const total = aggregation?.totalHeldTaskCount || 0;

  if (!loading && account && total > 0) {
    const steps = utils.getSteps(account, previous.current);

    const numOfStepsDone = steps.filter(step => step.isDone).length;
    const numOfStepsLeft = steps.length - numOfStepsDone;

    const title = utils.getHeldTaskBannerTitle(
      numOfStepsDone,
      numOfStepsLeft,
      total
    );

    const { daysRemainText, msg } = utils.getDaysRemainingMessage(
      aggregation?.startsExpiringOn
    );

    const buttonText = numOfStepsDone > 0 ? 'Fix the Rest' : 'Help Me Fix It';

    return {
      buttonText,
      daysRemainText,
      isHidden: false,
      msg,
      numOfStepsDone,
      numOfStepsLeft,
      steps,
      title,
    };
  }

  previous.current = aggregation;

  return {
    buttonText: '',
    daysRemainText: '',
    isHidden: true,
    msg: '',
    numOfStepsDone: 0,
    numOfStepsLeft: 0,
    steps: [],
    title: '',
  };
}

export default useHeldTasks;
